import { T } from 'src/shared/helpers';
import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';

// CSS globale per i dropdown negli overlay panels
const OverlayDropdownStyles = createGlobalStyle`
	/* Forza i dropdown del PropertiesPanel a essere sopra gli overlay */
	/* I menu di DropdownActions (role="menu" dal 2026-10-22) hanno già la loro superficie: restano fuori. */
	.react-select__menu,
	.react-select__menu-list,
	[role="listbox"],
	[role="menu"]:not([data-dropdown-actions]),
	[class*="AdvancedSelect"] [role="listbox"] {
		z-index: 999999 !important;
		position: fixed !important;
		background-color: white !important;
		border: 1px solid #ccc !important;
		border-radius: 4px !important;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
		margin-top: 2px !important;
		min-width: auto !important;
		width: auto !important;
	}
	
	/* Stili per le opzioni del dropdown */
	.react-select__option,
	[role="listbox"] [role="option"],
	[role="menu"]:not([data-dropdown-actions]) [role="menuitem"] {
		background-color: white !important;
		color: #333 !important;
		padding: 8px 12px !important;
		border-bottom: none !important;
		white-space: nowrap !important;
		overflow: hidden !important;
		text-overflow: ellipsis !important;
	}
	
	/* Hover sui dropdown */
	.react-select__option:hover,
	.react-select__option--is-focused,
	[role="listbox"] [role="option"]:hover,
	[role="menu"]:not([data-dropdown-actions]) [role="menuitem"]:hover {
		background-color: var(--editor-field, #f1f2f4) !important;
		color: var(--editor-fg, #262a30) !important;
	}
	
	/* Opzione selezionata */
	.react-select__option--is-selected,
	[role="listbox"] [role="option"][aria-selected="true"],
	[role="menu"]:not([data-dropdown-actions]) [role="menuitem"][aria-selected="true"] {
		background-color: #e6f3ff !important;
		color: #333 !important;
	}
	
	/* Lista del menu */
	.react-select__menu-list,
	[role="listbox"],
	[role="menu"]:not([data-dropdown-actions]) {
		padding: 0 !important;
		max-height: 200px !important;
		overflow-y: auto !important;
		background-color: white !important;
	}
	
	/* Assicurati che i menu portal siano sopra tutto */
	.react-select__menu-portal {
		z-index: 999999 !important;
	}
	
	/* CSS per forzare i dropdown a usare il portal */
	.react-select__control {
		position: relative;
		z-index: 1;
	}
	
	/* Forza la larghezza del dropdown a essere uguale al controllo */
	.react-select__menu {
		min-width: 100% !important;
		width: max-content !important;
		max-width: 200px !important;
	}
`;

const FloatingWrapper = styled.div`
    position: fixed;
    top: 100px;
    left: 390px;
    z-index: 99999;
    pointer-events: none;
    height: calc(100% - 100px); // calcolando anche il top
`;

const FloatingPanel = styled.div`
    position: relative;
    width: 500px;
    background-color: white;
    box-shadow: 4px 0 10px rgba(0, 0, 0, 0.2);
    border-radius: 0 8px 8px 0;
    pointer-events: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: visible;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #333;
    transition: color 0.2s;

    &:hover {
        color: #000;
    }
    z-index: 10000; // Assicurati che il pulsante sia sopra il contenuto del pannello
`;

interface OverlayPanelProps {
    children: ReactNode;
    onClose?: () => void;
}

const OverlayPanel = ({ children, onClose }: OverlayPanelProps) => {
    // Crea un portal target per i dropdown se non esiste
    useEffect(() => {
        const portalId = 'dropdown-portal-target';
        if (!document.getElementById(portalId)) {
            const portalDiv = document.createElement('div');
            portalDiv.id = portalId;
            portalDiv.style.position = 'fixed';
            portalDiv.style.top = '0';
            portalDiv.style.left = '0';
            portalDiv.style.zIndex = '999999';
            portalDiv.style.pointerEvents = 'none';
            document.body.appendChild(portalDiv);

            // Imposta globalmente il menuPortalTarget per React Select
            (window as any).dropdownPortalTarget = portalDiv;
        }

        return () => {
            // Non rimuovere il portal in cleanup perché potrebbe essere usato da altri overlay
        };
    }, []);

    return createPortal(
        <>
            <OverlayDropdownStyles />
            <FloatingWrapper>
                <FloatingPanel>
                    <CloseButton onClick={onClose} aria-label={T._('Close panel', 'Admin')}>
                        ×
                    </CloseButton>
                    <ScrollableContent>{children}</ScrollableContent>
                </FloatingPanel>
            </FloatingWrapper>
        </>,
        document.body
    );
};

export default OverlayPanel;
